import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";

const DEFAULT_SHOP_KEY = "default";
const AVAILABILITY_WINDOW_DAYS = 14;

export type LandingPackage = {
  id: number;
  name_ar: string;
  short_description_ar?: string | null;
  price: number;
  landing_order: number;
  badge_enabled: boolean;
  badge_label_ar?: string | null;
  delivery_enabled: boolean;
  allow_pickup: boolean;
  pickup_fee?: number | null;
  allow_dropoff: boolean;
  dropoff_fee?: number | null;
  service_id?: number | null;
  features_ar?: string[] | null;
};

export type AvailabilityDay = {
  date: string;
  label: string;
  status: "full" | "available";
  slotsLeft: number;
};

async function fetchLandingServices(shopKey = DEFAULT_SHOP_KEY) {
  const { data, error } = await supabase
    .from("services")
    .select(
      "id,name,description,base_price,duration_minutes,landing_price,landing_duration,landing_order,landing_short_description,image_path"
    )
    .eq("shop_key", shopKey)
    .eq("is_public", true)
    .eq("is_deleted", false)
    .order("landing_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    throw new Error("تعذر تحميل الخدمات حالياً");
  }

  return data ?? [];
}

async function fetchAvailability(shopKey = DEFAULT_SHOP_KEY) {
  // 1) Booking limit
  let maxBookings = 10;
  const { data: settingsData } = await supabase
    .from("app_settings")
    .select("daily_booking_limit")
    .eq("shop_key", shopKey)
    .maybeSingle();
  if (settingsData?.daily_booking_limit) {
    maxBookings = settingsData.daily_booking_limit;
  }

  // 2) Build date range (start tomorrow for 14 days)
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() + 1);
  const dates: string[] = [];
  for (let i = 0; i < AVAILABILITY_WINDOW_DAYS; i += 1) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    dates.push(d.toISOString().split("T")[0]);
  }
  const startDateStr = dates[0];
  const endDateStr = dates[dates.length - 1];

  // 3) Fetch booking counts using RLS-safe database function
  const { data: bookingCounts, error: bookingsError } = await supabase.rpc(
    "get_booking_availability",
    {
      p_shop_key: shopKey,
      p_start_date: startDateStr,
      p_end_date: endDateStr,
    }
  );

  if (bookingsError) {
    throw new Error("تعذر تحميل المواعيد المتاحة. يرجى المحاولة مرة أخرى.");
  }

  // 4) Aggregate counts from function result
  const counts: Record<string, number> = {};
  bookingCounts?.forEach((row: { booking_date: string; booking_count: number }) => {
    counts[row.booking_date] = row.booking_count;
  });

  // 5) Build day list
  const dateFormatter = new Intl.DateTimeFormat("ar-EG", {
    weekday: "long",
    day: "numeric",
    month: "short",
    numberingSystem: "latn", // keep digits western, labels Arabic
  });

  const days: AvailabilityDay[] = dates.map((dateStr) => {
    const d = new Date(dateStr);
    const count = counts[dateStr] || 0;
    const isFull = count >= maxBookings;
    return {
      date: dateStr,
      label: dateFormatter.format(d),
      status: isFull ? "full" : "available",
      slotsLeft: Math.max(0, maxBookings - count),
    };
  });

  return days;
}

export function useLandingServicesQuery(shopKey = DEFAULT_SHOP_KEY) {
  return useQuery({
    queryKey: ["landing-services", shopKey],
    queryFn: () => fetchLandingServices(shopKey),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAvailabilityQuery(shopKey = DEFAULT_SHOP_KEY) {
  return useQuery<AvailabilityDay[]>({
    queryKey: ["availability", shopKey],
    queryFn: () => fetchAvailability(shopKey),
    staleTime: 30 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

async function fetchLandingPackages(shopKey = DEFAULT_SHOP_KEY) {
  const { data, error } = await supabase
    .from("packages")
    .select(
      "id,name_ar,short_description_ar,price,landing_order,badge_enabled,badge_label_ar,delivery_enabled,allow_pickup,pickup_fee,allow_dropoff,dropoff_fee,service_id,features_ar"
    )
    .eq("shop_key", shopKey)
    .eq("is_active", true)
    .eq("is_deleted", false)
    .order("landing_order", { ascending: true })
    .order("id", { ascending: true });

  if (error) {
    throw new Error("تعذر تحميل الباقات حالياً");
  }

  return (data as LandingPackage[]) ?? [];
}

export function useLandingPackagesQuery(shopKey = DEFAULT_SHOP_KEY) {
  return useQuery({
    queryKey: ["landing-packages", shopKey],
    queryFn: () => fetchLandingPackages(shopKey),
    staleTime: 5 * 60 * 1000,
  });
}

export type LandingWork = {
  id: number;
  platform: "tiktok" | "facebook";
  video_id?: string | null;
  video_url?: string | null;
  thumbnail_url?: string | null;
  title_ar: string;
  description_ar?: string | null;
  sort_order: number;
};

async function fetchLandingWorks(shopKey = DEFAULT_SHOP_KEY) {
  const { data, error } = await supabase
    .from("works")
    .select(
      "id,platform,video_id,video_url,thumbnail_url,title_ar,description_ar,sort_order"
    )
    .eq("shop_key", shopKey)
    .eq("is_active", true)
    .eq("is_deleted", false)
    .order("sort_order", { ascending: true })
    .order("id", { ascending: true });

  if (error) {
    throw new Error("تعذر تحميل الأعمال حالياً");
  }

  return (data as LandingWork[]) ?? [];
}

export function useLandingWorksQuery(shopKey = DEFAULT_SHOP_KEY) {
  return useQuery({
    queryKey: ["landing-works", shopKey],
    queryFn: () => fetchLandingWorks(shopKey),
    staleTime: 5 * 60 * 1000,
  });
}

// Landing CMS visibility settings
type LandingPageSettings = {
  show_hero: boolean;
  show_brands: boolean;
  show_works: boolean;
  show_packages: boolean;
  show_before_after: boolean;
  show_why_us: boolean;
  show_how_it_works: boolean;
  show_stats: boolean;
  show_booking_cta: boolean;
  show_location: boolean;
  show_faq: boolean;
  show_about: boolean;
};

async function fetchLandingPageSettings(shopKey = DEFAULT_SHOP_KEY) {
  const { data, error } = await supabase
    .from("landing_page_settings")
    .select(
      "show_hero,show_brands,show_works,show_packages,show_before_after,show_why_us,show_how_it_works,show_stats,show_booking_cta,show_location,show_faq,show_about"
    )
    .eq("shop_key", shopKey)
    .maybeSingle();

  if (error) {
    throw new Error("تعذر تحميل إعدادات الواجهة حالياً");
  }

  // Fallback if no row exists: show all sections by default
  return (
    (data as LandingPageSettings | null) ?? {
      show_hero: true,
      show_brands: true,
      show_works: true,
      show_packages: true,
      show_before_after: true,
      show_why_us: true,
      show_how_it_works: true,
      show_stats: true,
      show_booking_cta: true,
      show_location: true,
      show_faq: true,
      show_about: true,
    }
  );
}

export function useLandingPageSettingsQuery(shopKey = DEFAULT_SHOP_KEY) {
  return useQuery<LandingPageSettings>({
    queryKey: ["landing-page-settings", shopKey],
    queryFn: () => fetchLandingPageSettings(shopKey),
    staleTime: 5 * 60 * 1000,
  });
}

